import {
  SelectChangeEvent,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Grid,
  Typography,
  Autocomplete,
} from "@mui/material";
import React, { ChangeEvent, useState, useEffect } from "react";
import { FormData } from "..";
import { UsuarioFoto } from "../UsuarioFoto";
import InputMask from "react-input-mask";
import { opcoesProfissao } from "./Profissoes";
import { validarCNPJ, validarCPF } from "../../../../Utils/Validadores";
import { CampoData } from "./CampoData";
import { dataBrEhValida, formatarDataParaBR } from "../../../../Utils/FormataData";




interface Props {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  formValid: boolean;
  setFormValid: React.Dispatch<React.SetStateAction<boolean>>;
}

export const DadosPessoais = ({
  formData,
  setFormData,
  formValid,
  setFormValid,
}: Props) => {
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [descricaoOutros, setDescricaoOutros] = useState("");

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      let updatedData = { ...prev, [name]: value };

      if (name === "tipoPessoa") {
        updatedData = {
          ...updatedData,
          cpf: value === "F" ? "" : prev.cpf,
          cnpj: value === "J" ? "" : prev.cnpj,
          sexo: value === "F" ? prev.sexo : "", // Reseta sexo se mudar para Jurídica
          dataNascimento: value === "F" ? prev.dataNascimento : "",
        };
      }

      if (name === "cpf") {
        updatedData.cpf = value.replace(/\D/g, "").slice(0, 11);
      }
      if (name === "cnpj") {
        updatedData.cnpj = value.replace(/\D/g, "").slice(0, 14);
      }

      return updatedData;
    });
  };

  useEffect(() => {
    const errors: { [key: string]: string } = {};

    if (!formData.nome) {
      errors.nome = "Nome é obrigatório";
    } else if (!/^\S+\s+\S+/.test(formData.nome)) {
      errors.nome = "Informe pelo menos um sobrenome";
    }

    if (formData.tipoPessoa === "F") {
      if (!/^\d{11}$/.test(formData.cpf) || !validarCPF(formData.cpf)) {
        errors.cpf = "CPF inválido";
      }
    }

    if (formData.tipoPessoa === "J") {
      if (!/^\d{14}$/.test(formData.cnpj) || !validarCNPJ(formData.cnpj)) {
        errors.cnpj = "CNPJ inválido";
      }
    }
    if (!formData.dataNascimento) {
      errors.dataNascimento =
        formData.tipoPessoa === "F"
          ? "Data de nascimento obrigatória"
          : "Data de fundação obrigatória";
    } else {
      const dataISO = formData.dataNascimento;
      const [ano, mes, dia] = dataISO.split('-'); // yyyy-mm-dd
      const dataBr = `${dia}/${mes}/${ano}`; // transforma em dd/mm/yyyy

      const regex = /^\d{2}\/\d{2}\/\d{4}$/;
      if (!regex.test(dataBr)) {
        errors.dataNascimento = "Data inválida";
      } else {
        const data = new Date(Number(ano), Number(mes) - 1, Number(dia));
        const dataEhValida =
          data.getFullYear() === Number(ano) &&
          data.getMonth() === Number(mes) - 1 &&
          data.getDate() === Number(dia);

        if (!dataEhValida) {
          errors.dataNascimento = "Data inválida";
        } else {
          const hoje = new Date();
          hoje.setHours(0, 0, 0, 0); // zera horário para comparação precisa

          if (data > hoje) {
            errors.dataNascimento = "Data não pode ser no futuro";
          }
        }

      }
    }



    if (formData.tipoPessoa === "F" && !formData.sexo)
      errors.sexo = "Selecione um sexo";

    setFormErrors(errors);
    setFormValid(Object.keys(errors).length === 0);
  }, [formData, setFormValid]);


  const InputMaskCustom = React.forwardRef<HTMLInputElement, any>((props, ref) => {
    return (
      <InputMask {...props} mask="99/99/9999" placeholder="dd/mm/aaaa">
        {(inputProps: any) => <input {...inputProps} ref={ref} />}
      </InputMask>
    );
  });

  return (
    <Box p={0}>
      <UsuarioFoto />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Nome Completo"
            name="nome"
            value={formData.nome}
            onChange={handleChange}
            margin="normal"
            inputProps={{ maxLength: 250 }}
            error={!!formErrors.nome}
            helperText={formErrors.nome}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Tipo de Pessoa</InputLabel>
            <Select
              name="tipoPessoa"
              value={formData.tipoPessoa}
              onChange={handleChange}
            >
              <MenuItem value="F">Pessoa Física</MenuItem>
              <MenuItem value="J">Pessoa Jurídica</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {formData.tipoPessoa === "F" && (
          <>
            <Grid item xs={12} sm={6}>
              <InputMask
                mask="999.999.999-99"
                value={formData.cpf}
                onChange={handleChange}
              >
                {() => (
                  <TextField
                    fullWidth
                    label="CPF"
                    name="cpf"
                    margin="normal"
                    error={!!formErrors.cpf}
                    helperText={formErrors.cpf}
                  />
                )}
              </InputMask>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Sexo</InputLabel>
                <Select
                  name="sexo"
                  value={formData.sexo}
                  onChange={handleChange}
                  error={!!formErrors.sexo}
                >
                  <MenuItem value="M">Masculino</MenuItem>
                  <MenuItem value="F">Feminino</MenuItem>
                  <MenuItem value="N">Não Declarar</MenuItem>
                </Select>
                <Typography variant="caption" color="error">
                  {formErrors.sexo}
                </Typography>
              </FormControl>
            </Grid>
          </>
        )}

        {formData.tipoPessoa === "J" && (
          <Grid item xs={12} sm={6}>
            <InputMask
              mask="99.999.999/9999-99"
              value={formData.cnpj}
              onChange={handleChange}
            >
              {() => (
                <TextField
                  fullWidth
                  label="CNPJ"
                  name="cnpj"
                  margin="normal"
                  error={!!formErrors.cnpj}
                  helperText={formErrors.cnpj}
                />
              )}
            </InputMask>
          </Grid>
        )}

        {/* Data de Nascimento para Pessoa Física e Data de Fundação para Jurídica */}
        <Grid item xs={12} sm={6}>
          <CampoData
            value={formData.dataNascimento}
            onChange={handleChange}
            error={formErrors.dataNascimento}
            tipoPessoa={formData.tipoPessoa === "F" ? "FISICA" : "JURIDICA"} // 👈 aqui
          />



        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            freeSolo
            options={opcoesProfissao}
            value={formData.descricao}
            onChange={(event, newValue) => {
              // Quando selecionar algo da lista
              setFormData((prev) => ({
                ...prev,
                descricao: newValue || ""
              }));
            }}
            onInputChange={(event, newInputValue) => {
              // Quando digitar algo manualmente
              setFormData((prev) => ({
                ...prev,
                descricao: newInputValue
              }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Selecione ou digite sua profissão/ocupação"
                margin="normal"
                fullWidth
              />
            )}
          />
        </Grid>





      </Grid>
    </Box>
  );
};
